import { useCallback, useEffect, useRef, useState } from "react";
import { streamAssistantVoiceTtsAudio } from "api";
import { int16ToFloat32, TTS_SAMPLE_RATE } from "./pcmAudio";
import { takeSpeakableFragments } from "./speakBatching";

// Streams text fragments through the backend ElevenLabs proxy
// (POST /assistant/voice/tts, raw linear16 mono 24 kHz) and plays the audio
// gaplessly via Web Audio scheduling. Same public interface as the Deepgram
// Aura player (useAuraTtsPlayer) so useVoiceConversation can swap providers
// with one import.
//
// Ordering: each flushed fragment becomes one utterance. Its fetch starts
// immediately (pipelined), but audio is consumed and scheduled strictly in
// enqueue order through a promise chain. stopAndClear() bumps a generation
// counter and aborts in-flight fetches so cancelled speech never plays.
const useElevenLabsTtsPlayer = ({ onPlaybackStart, onPlaybackEnd, onError }) => {
  const [isSpeaking, setIsSpeaking] = useState(false);

  const audioContextRef = useRef(null);
  const gainNodeRef = useRef(null);
  const analyserRef = useRef(null);
  const playheadRef = useRef(0);
  const activeSourcesRef = useRef(new Set());
  const speakingRef = useRef(false);

  const textBufferRef = useRef("");
  const generationRef = useRef(0);
  const chainRef = useRef(Promise.resolve());
  const abortControllersRef = useRef(new Set());
  const outstandingUtterancesRef = useRef(0);
  const finalizedRef = useRef(false);

  const callbacksRef = useRef({});
  callbacksRef.current = { onPlaybackStart, onPlaybackEnd, onError };

  const ensureAudioContext = useCallback(() => {
    if (!audioContextRef.current) {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const gainNode = audioContext.createGain();
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 128;
      gainNode.connect(analyser);
      analyser.connect(audioContext.destination);
      audioContextRef.current = audioContext;
      gainNodeRef.current = gainNode;
      analyserRef.current = analyser;
    }
    if (audioContextRef.current.state === "suspended") {
      audioContextRef.current.resume().catch(() => {});
    }
    return audioContextRef.current;
  }, []);

  const markPlaybackEndIfDrained = useCallback(() => {
    if (!speakingRef.current) return;
    if (
      activeSourcesRef.current.size > 0 ||
      outstandingUtterancesRef.current > 0 ||
      !finalizedRef.current
    ) {
      return;
    }
    speakingRef.current = false;
    finalizedRef.current = false;
    setIsSpeaking(false);
    callbacksRef.current.onPlaybackEnd?.();
  }, []);

  const scheduleAudioChunk = useCallback(
    (arrayBuffer) => {
      const audioContext = ensureAudioContext();
      const samples = int16ToFloat32(arrayBuffer);
      if (!samples.length) return;

      const buffer = audioContext.createBuffer(1, samples.length, TTS_SAMPLE_RATE);
      buffer.copyToChannel(samples, 0);

      const source = audioContext.createBufferSource();
      source.buffer = buffer;
      source.connect(gainNodeRef.current);

      const startAt = Math.max(audioContext.currentTime + 0.05, playheadRef.current);
      playheadRef.current = startAt + buffer.duration;
      activeSourcesRef.current.add(source);
      source.onended = () => {
        activeSourcesRef.current.delete(source);
        markPlaybackEndIfDrained();
      };
      source.start(startAt);

      if (!speakingRef.current) {
        speakingRef.current = true;
        setIsSpeaking(true);
        callbacksRef.current.onPlaybackStart?.();
      }
    },
    [ensureAudioContext, markPlaybackEndIfDrained],
  );

  const enqueueUtterance = useCallback(
    (text) => {
      if (!text.trim()) return;
      const generation = generationRef.current;
      finalizedRef.current = false;
      outstandingUtterancesRef.current += 1;

      const abortController = new AbortController();
      abortControllersRef.current.add(abortController);

      // The fetch starts now (pipelined), but audio may only reach the
      // scheduler when this utterance's turn comes in the chain — chunks
      // arriving early are buffered, otherwise sentences would interleave
      // on the shared playhead and play "in parallel".
      const utterance = { live: false, pendingChunks: [] };
      const deliverAudio = (arrayBuffer) => {
        if (generationRef.current !== generation) return;
        if (utterance.live) {
          scheduleAudioChunk(arrayBuffer);
        } else {
          utterance.pendingChunks.push(arrayBuffer);
        }
      };

      // PCM chunks can split an Int16 sample across network chunks; carry the
      // odd byte over to the next chunk.
      let leftoverByte = null;
      const handleChunk = (chunk) => {
        if (generationRef.current !== generation) return;
        let bytes = chunk;
        if (leftoverByte !== null) {
          const joined = new Uint8Array(bytes.byteLength + 1);
          joined[0] = leftoverByte;
          joined.set(bytes, 1);
          bytes = joined;
          leftoverByte = null;
        }
        const evenLength = bytes.byteLength - (bytes.byteLength % 2);
        if (bytes.byteLength % 2 === 1) {
          leftoverByte = bytes[bytes.byteLength - 1];
        }
        if (evenLength > 0) {
          deliverAudio(bytes.slice(0, evenLength).buffer);
        }
      };

      const streamPromise = streamAssistantVoiceTtsAudio({
        text,
        signal: abortController.signal,
        onChunk: handleChunk,
      });
      streamPromise.catch(() => {});

      chainRef.current = chainRef.current
        .then(async () => {
          if (generationRef.current !== generation) {
            abortController.abort();
            return;
          }
          // This utterance's turn: flush what already arrived, then schedule
          // the rest live as the stream continues.
          utterance.live = true;
          utterance.pendingChunks.forEach((arrayBuffer) =>
            scheduleAudioChunk(arrayBuffer),
          );
          utterance.pendingChunks = [];
          await streamPromise;
        })
        .catch((error) => {
          if (error?.name !== "AbortError" && !abortController.signal.aborted) {
            callbacksRef.current.onError?.(error);
          }
        })
        .finally(() => {
          abortControllersRef.current.delete(abortController);
          outstandingUtterancesRef.current = Math.max(
            0,
            outstandingUtterancesRef.current - 1,
          );
          markPlaybackEndIfDrained();
        });
    },
    [scheduleAudioChunk, markPlaybackEndIfDrained],
  );

  // Queues delta text; complete sentences (or long fragments) become
  // utterances immediately, the tail waits for more text or flush().
  const speak = useCallback(
    (deltaText) => {
      if (typeof deltaText !== "string" || !deltaText) return;
      const { sendable, remainder } = takeSpeakableFragments(
        textBufferRef.current + deltaText,
      );
      textBufferRef.current = remainder;
      if (sendable) {
        enqueueUtterance(sendable);
      }
    },
    [enqueueUtterance],
  );

  // Sends any tail text and marks the current speech finished: once every
  // queued utterance has streamed and every scheduled source has ended,
  // onPlaybackEnd fires.
  const flush = useCallback(() => {
    const tail = textBufferRef.current;
    textBufferRef.current = "";
    if (tail.trim()) {
      enqueueUtterance(tail);
    }
    const generation = generationRef.current;
    chainRef.current = chainRef.current.then(() => {
      if (generationRef.current !== generation) return;
      finalizedRef.current = true;
      markPlaybackEndIfDrained();
    });
  }, [enqueueUtterance, markPlaybackEndIfDrained]);

  const stopAndClear = useCallback(() => {
    generationRef.current += 1;
    textBufferRef.current = "";
    finalizedRef.current = false;

    abortControllersRef.current.forEach((controller) => controller.abort());
    abortControllersRef.current.clear();

    activeSourcesRef.current.forEach((source) => {
      source.onended = null;
      try {
        source.stop();
      } catch (error) {
        // Already stopped.
      }
    });
    activeSourcesRef.current.clear();
    playheadRef.current = 0;

    if (speakingRef.current) {
      speakingRef.current = false;
      setIsSpeaking(false);
    }
  }, []);

  const disconnect = useCallback(() => {
    stopAndClear();
    audioContextRef.current?.close().catch(() => {});
    audioContextRef.current = null;
    gainNodeRef.current = null;
    analyserRef.current = null;
  }, [stopAndClear]);

  const getOutputAnalyser = useCallback(() => analyserRef.current, []);

  useEffect(() => () => disconnect(), [disconnect]);

  return {
    speak,
    flush,
    stopAndClear,
    disconnect,
    isSpeaking,
    getOutputAnalyser,
  };
};

export default useElevenLabsTtsPlayer;
